import type { StationWithOrganization } from '~/types/common/stationWithOrganization';

/**
 * 駅情報に関する処理
 *
 * @param displayId 画面ID
 */
export const useStation = (displayId: string) => {
  const { getStations, getOrganizations } = useMasterStore();

  /**
   * 駅コードから駅マスタ情報を取得する
   *
   * @param stationCd 駅コード
   * @param stationSubCd 社線駅サブコード
   * @returns 駅マスタ情報
   */
  const _getStationByCd = async (stationCd: string, stationSubCd?: string) => {
    const stations = await getStations(displayId);
    return stations.find((st) => st.stationCd === stationCd && st.stationSubCd === (stationSubCd ?? ''));
  };

  /**
   * 組織コードから支社マスタ情報を取得する
   *
   * @param organizationCd 組織コード
   * @returns 支社マスタ情報
   */
  const _getOrganizationByCd = async (organizationCd: string) => {
    const organizations = await getOrganizations(displayId);
    return organizations.find((og) => og.organizationCd === organizationCd && og.organizationKbn === '1');
  };

  /**
   * 駅コードから駅マスタとそれに紐づく支社マスタを取得する
   *
   * @param stationCd 駅コード
   * @param stationSubCd 社線駅サブコード
   * @returns 駅マスタとそれに紐づく支社マスタ
   */
  const getStationAndOrganizationByStationCd = async (stationCd: string, stationSubCd?: string) => {
    const station = await _getStationByCd(stationCd, stationSubCd);
    const organization = await _getOrganizationByCd(station?.organizationCd ?? '');

    if (!station || !organization) {
      return undefined;
    }

    return {
      station,
      organization,
    };
  };

  /**
   * 駅コードから支社情報付き駅情報を取得する
   *
   * @param stationCd 駅コード
   * @param stationSubCd 社線駅サブコード
   * @returns 支社情報付き駅情報
   */
  const getStationWithOrganizationByStationCd = async (
    stationCd: string,
    stationSubCd?: string,
  ): Promise<StationWithOrganization | undefined> => {
    const result = await getStationAndOrganizationByStationCd(stationCd, stationSubCd);
    if (!result) {
      return undefined;
    }

    return {
      organizationCd: result.organization.organizationCd,
      organizationName: result.organization.organizationName,
      stationCd: result.station.stationCd,
      stationSubCd: result.station.stationSubCd,
      stationName: result.station.stationName,
      stationNameRyaku: result.station.stationNameRyaku,
    };
  };

  return {
    getStationAndOrganizationByStationCd,
    getStationWithOrganizationByStationCd,
  };
};
